import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SessionContext = createContext(null);

const STORAGE_KEY = 'school_session';
const CHILD_STORAGE_KEY = 'school_selected_child';

function readStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error(`Failed to parse localStorage item: ${key}`, err);
    return null;
  }
}

export function SessionProvider({ children }) {
  const [user, setUser] = useState(() => readStorage(STORAGE_KEY));
  const [selectedChild, setSelectedChildState] = useState(() => readStorage(CHILD_STORAGE_KEY));

  const login = useCallback((userData) => {
    setUser(userData);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
    } catch (err) {
      console.error('Failed to store session', err);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setSelectedChildState(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(CHILD_STORAGE_KEY);
    } catch (err) {
      console.error('Failed to clear session', err);
    }
  }, []);

  const setSelectedChild = useCallback((child) => {
    setSelectedChildState(child);
    try {
      if (child) {
        localStorage.setItem(CHILD_STORAGE_KEY, JSON.stringify(child));
      } else {
        localStorage.removeItem(CHILD_STORAGE_KEY);
      }
    } catch (err) {
      console.error('Failed to store selected child', err);
    }
  }, []);

  const value = {
    user,
    role: user?.role || null,
    isLoggedIn: !!user,
    login,
    logout,
    selectedChild,
    setSelectedChild
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return ctx;
}
